import { FC } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge, GoBack } from '@/_shared/components';
import { WizardNavigationButton } from '@/_shared/components/Wizard/types';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { postSurveysCampaignByIdNextCall } from '@/surveys/state/api/postSurveysCampaignByIdNextCall';
import { DialogConfirmExitFromSurvey } from './DialogConfirmExitFromSurvey';
import { DynamicSurvey } from './DynamicSurvey';
import { useEmployeeCompleteSurvey } from './hook/useEmployeeCompleteSurvey';

type Props = { wizardNavigationButton: WizardNavigationButton };

export const EmployeeCompleteSurvey: FC<Props> = ({ wizardNavigationButton }) => {
  const t = useTranslations('configuration.learning.employeeCompleteSurvey');
  const tShared = useTranslations();
  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();
  const { openDialog } = useDialog('confirmExitFromSurveyDialog');

  const { error: errorPostSurveysCampaignByIdNext } = postSurveysCampaignByIdNextCall.hook();

  const gapXXXL = convertSizeToNumber(`${useThemeValue('sizes', 'xxxl')}`) || 64;
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`) || 12;

  const { currentStep, control } = useEmployeeCompleteSurvey();

  return (
    <Box>
      <GoBack label={t('exit')} onBack={openDialog} />
      <DialogConfirmExitFromSurvey />
      <Box
        display='flex'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        alignItems='flex-start'
        justifyContent='center'
        py={genericResponsiveValue({
          breakPoints: { desktop: 'xl' },
          defaultValue: 'm',
        })}
        width='100%'
        style={{ gap: isDesktop ? gapXXXL : undefined }}
        flex={10}
      >
        <Box
          flex={genericResponsiveValue({
            breakPoints: { desktop: 5 },
            defaultValue: 10,
          })}
        >
          <Typography
            variant='h2'
            fontSize={genericResponsiveValue({
              breakPoints: { desktop: 50 as never },
              defaultValue: 'xxxl',
            })}
            lineHeight={genericResponsiveValue({
              breakPoints: { desktop: '60px' as never },
              defaultValue: 'xxxl',
            })}
            fontWeight='bold'
            mb={genericResponsiveValue({
              breakPoints: { desktop: 'm' },
              defaultValue: 'xs',
            })}
          >
            {t('title')}
          </Typography>
          <Typography variant='p1/light' mb='l' color='grey/light'>
            {t('subtitle')}
          </Typography>
        </Box>
        <Box
          flex={genericResponsiveValue({
            breakPoints: { desktop: 5 },
            defaultValue: 10,
          })}
          width='100%'
        >
          <Card width='100%'>
            {errorPostSurveysCampaignByIdNext ? (
              <AlertBadge iconName='info' type='error' label={tShared('errors.genericApiError')} />
            ) : (
              <DynamicSurvey currentStep={currentStep || {}} control={control} />
            )}
          </Card>
        </Box>
      </Box>
      <Box mt='xs'>
        <Box
          display='flex'
          justifyContent={genericResponsiveValue({
            breakPoints: { desktop: 'flex-end' },
            defaultValue: 'flex-start',
          })}
          style={{ gap: gapXS }}
        >
          {currentStep?.stepNumber && currentStep?.stepNumber > 0 ? (
            <wizardNavigationButton.Previous />
          ) : null}
          <wizardNavigationButton.Next />
        </Box>
      </Box>
    </Box>
  );
};
